import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { apiClient } from '../../api/HttpClient';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AlertCircle, Loader2, Ticket, InboxIcon } from 'lucide-react';

interface Ticket {
  id: string;
  tenant_name: string;
  room_number: string;
  bed_number: string;
  property_id: string;
  message: string;
  status: string;
  created_at: string;
}

interface TicketDashboardProps {
  propertyId: string;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  open: {
    label: 'Open',
    className: 'bg-orange-100 text-orange-700 border border-orange-200',
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-blue-100 text-blue-700 border border-blue-200',
  },
  closed: {
    label: 'Closed',
    className: 'bg-green-100 text-green-700 border border-green-200',
  },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? {
    label: status,
    className: 'bg-muted text-muted-foreground border border-border',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.className}`}>
      {cfg.label}
    </span>
  );
};

const formatDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const TicketDashboard: React.FC<TicketDashboardProps> = ({ propertyId }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [monthFilter, setMonthFilter] = useState(dayjs().format('YYYY-MM'));

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const client = await apiClient;
      const response = await client.get<Ticket[]>(
        `/api/pg_tenant/tickets/?property_id=${propertyId}&status=${statusFilter}`
      );
      setTickets(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load tickets.');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [propertyId, statusFilter]);

  const handleStatusChange = async (ticketId: string, newStatus: string) => {
    setUpdatingId(ticketId);
    try {
      const client = await apiClient;
      await client.put(`/api/pg_tenant/tickets/${ticketId}/`, { status: newStatus });
      setTickets((prev) =>
        prev.map((t) => (t.id === ticketId ? { ...t, status: newStatus } : t))
      );
    } catch (err) {
      console.error('Failed to update ticket status:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  // ── Month-filtered view ──────────────────────────────────────────────────
  const filteredTickets = tickets.filter((t) =>
    t.created_at.startsWith(monthFilter)
  );

  // ── Stats derived from all tickets ──────────────────────────────────────
  const openCount       = tickets.filter((t) => t.status === 'open').length;
  const inProgressCount = tickets.filter((t) => t.status === 'in_progress').length;
  const closedCount     = tickets.filter((t) => t.status === 'closed').length;

  return (
    <div className="space-y-5">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Ticket className="h-5 w-5 text-orange-500" />
          <h2 className="text-lg font-semibold text-foreground">Tenant Tickets</h2>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Month</label>
            <Select value={monthFilter} onValueChange={setMonthFilter}>
              <SelectTrigger className="w-[120px] bg-background">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="bg-card border z-50">
                {Array.from({ length: 13 }, (_, i) => dayjs().subtract(i, 'month').format('YYYY-MM')).map(month => (
                  <SelectItem key={month} value={month}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-background">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-card border z-50">
                <SelectItem value="all">All Tickets</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* ── Summary Cards ───────────────────────────────────────────────── */}
      {!loading && !error && (
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-3 text-center">
            <p className="text-2xl font-bold text-orange-600">{openCount}</p>
            <p className="text-xs text-orange-500 font-medium mt-0.5">Open</p>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-center">
            <p className="text-2xl font-bold text-blue-600">{inProgressCount}</p>
            <p className="text-xs text-blue-500 font-medium mt-0.5">In Progress</p>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{closedCount}</p>
            <p className="text-xs text-green-500 font-medium mt-0.5">Closed</p>
          </div>
        </div>
      )}

      {/* ── Loading ─────────────────────────────────────────────────────── */}
      {loading && (
        <div className="flex justify-center items-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {!loading && error && (
        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* ── Empty State ─────────────────────────────────────────────────── */}
      {!loading && !error && filteredTickets.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
          <InboxIcon className="h-10 w-10 opacity-30" />
          <p className="text-sm font-medium">No tickets found</p>
          <p className="text-xs opacity-70">
            {statusFilter === 'all'
              ? `No tickets found for ${monthFilter}.`
              : `No "${statusFilter}" tickets for ${monthFilter}.`}
          </p>
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────────────── */}
      {!loading && !error && filteredTickets.length > 0 && (
        <div className="border border-border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-primary hover:bg-primary">
                <TableHead className="text-primary-foreground font-semibold">Tenant</TableHead>
                <TableHead className="text-primary-foreground font-semibold">Room</TableHead>
                <TableHead className="text-primary-foreground font-semibold">Bed</TableHead>
                <TableHead className="text-primary-foreground font-semibold w-80">Message</TableHead>
                <TableHead className="text-primary-foreground font-semibold">Raised On</TableHead>
                <TableHead className="text-primary-foreground font-semibold">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTickets.map((ticket) => (
                <TableRow key={ticket.id} className="hover:bg-muted/50 align-top">
                  <TableCell className="font-medium text-foreground py-3">
                    {ticket.tenant_name}
                  </TableCell>
                  <TableCell className="text-muted-foreground py-3">
                    {ticket.room_number}
                  </TableCell>
                  <TableCell className="text-muted-foreground py-3">
                    {ticket.bed_number}
                  </TableCell>
                  <TableCell className="py-3 text-sm text-foreground max-w-xs">
                    <p className="whitespace-pre-wrap break-words leading-snug">
                      {ticket.message}
                    </p>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs py-3 whitespace-nowrap">
                    {formatDate(ticket.created_at)}
                  </TableCell>
                  <TableCell className="py-3">
                    <Select
                      value={ticket.status}
                      onValueChange={(val) => handleStatusChange(ticket.id, val)}
                      disabled={updatingId === ticket.id}
                    >
                      <SelectTrigger className="w-36 h-8 text-xs bg-background">
                        {updatingId === ticket.id ? (
                          <span className="flex items-center gap-1.5 text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                          </span>
                        ) : (
                          <StatusBadge status={ticket.status} />
                        )}
                      </SelectTrigger>
                      <SelectContent className="bg-card border z-50">
                        <SelectItem value="open">
                          <StatusBadge status="open" />
                        </SelectItem>
                        <SelectItem value="in_progress">
                          <StatusBadge status="in_progress" />
                        </SelectItem>
                        <SelectItem value="closed">
                          <StatusBadge status="closed" />
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default TicketDashboard;
